import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Grid,
  InputAdornment,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import StarIcon from '@mui/icons-material/Star';
import VerifiedIcon from '@mui/icons-material/Verified';
import { Helmet } from 'react-helmet-async';
import { useSearchParams } from 'react-router-dom';

import config from '@src/config';
import leadGurusLogo from '@src/assets/logo-full-dark.png';
import { toast } from '@src/components/core/toaster';
import {
  fetchProspectVerticals,
  fetchProspectEstimate,
  fetchProspectZipDemographics,
  fetchShortApplyPrefill,
  submitProspect,
} from '@src/api/actions/prospects';
import ProjectionZipMap from './components/ProjectionZipMap';
import ZipIntakeStep from './components/ZipIntakeStep';

const STEPS = ['Apply', 'Contact Info', 'Service Area', 'Verticals', 'Budget', 'Projections'];

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------
const fmt = (n) => (n == null ? '—' : Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 }));
const fmtCurrency = (n) => (n == null ? '—' : `$${Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 })}`);
const fmtPct = (n) => (n == null || !Number.isFinite(n) ? '—' : `${(Number(n) * 100).toFixed(1)}%`);
const fmtDec = (n) => (n == null || !Number.isFinite(n) ? '—' : Math.round(n).toLocaleString('en-US'));
const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function GetStartedPage() {
  const [activeStep, setActiveStep] = useState(0);

  // Step 0: Contact info
  const [contactForm, setContactForm] = useState({
    company_name: '',
    contact_name: '',
    contact_email: '',
    phone: '',
    website: '',
  });

  // Step 2: service area — cleaned rows with one territory per market
  const [zipState, setZipState] = useState({
    rows: [], territories: [], features: [], report: null, helpRequest: '', source: null,
  });
  const updateZipState = useCallback((patch) => setZipState((prev) => ({ ...prev, ...patch })), []);
  const zipRows = zipState.rows;
  const zipCount = zipState.report?.valid_zip_count || 0;
  const helpRequest = zipRows.length ? '' : (zipState.helpRequest || '').trim();

  // Step 2: Verticals (confirmation/correction)
  const [availableVerticals, setAvailableVerticals] = useState([]);
  const [selectedVerticals, setSelectedVerticals] = useState([]);

  // Step 3: Budget
  // For single brand: { [vertical]: amount }
  // For multi-brand: { [brand]: { [vertical]: amount } }
  const [verticalBudgets, setVerticalBudgets] = useState({});

  // Step 4: Projections
  const [projectionSummary, setProjectionSummary] = useState(null);
  const [demographics, setDemographics] = useState(null);
  const [isLoadingProjection, setIsLoadingProjection] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [projectionsUrl, setProjectionsUrl] = useState(null);

  // Arriving from the short-application email: prefill contact info and
  // attach this submission to that application.
  const [searchParams] = useSearchParams();
  const refToken = searchParams.get('ref') || '';
  const [prospectToken, setProspectToken] = useState('');
  useEffect(() => {
    if (!refToken) return;
    fetchShortApplyPrefill(refToken)
      .then((res) => {
        const d = res?.data || {};
        setProspectToken(refToken);
        setContactForm((prev) => ({
          company_name: d.company_name || prev.company_name,
          contact_name: d.contact_name || prev.contact_name,
          contact_email: d.contact_email || prev.contact_email,
          phone: d.phone || prev.phone,
          website: d.website || prev.website,
        }));
      })
      .catch(() => {});
  }, [refToken]);

  // Load verticals on mount
  useEffect(() => {
    fetchProspectVerticals()
      .then((res) => setAvailableVerticals(res?.data?.verticals || []))
      .catch(() => {});
  }, []);

  // Derived: brands detected from zip data
  const detectedBrands = useMemo(() => {
    const brands = [...new Set(zipRows.map((r) => r.brand).filter(Boolean))];
    return brands;
  }, [zipRows]);

  const isMultiBrand = detectedBrands.length > 1;

  // Derived: verticals detected from zip data (for pre-selection)
  const detectedVerticals = useMemo(() => {
    return [...new Set(zipRows.map((r) => r.vertical).filter(Boolean))];
  }, [zipRows]);

  // Pre-select verticals from ZIP data when moving to verticals step
  const initVerticals = useCallback(() => {
    if (!detectedVerticals.length) return;
    const matched = detectedVerticals
      .map((dv) => availableVerticals.find((av) => av.toLowerCase() === dv.toLowerCase()) || dv)
      .filter((v) => availableVerticals.includes(v));
    if (matched.length) setSelectedVerticals(matched);
  }, [detectedVerticals, availableVerticals]);

  // Budget totals
  const totalBudget = useMemo(() => {
    if (isMultiBrand) {
      return detectedBrands.reduce((sum, brand) => {
        const bBudgets = verticalBudgets[brand] || {};
        return sum + selectedVerticals.reduce((s, v) => s + (Number(bBudgets[v]) || 0), 0);
      }, 0);
    }
    return selectedVerticals.reduce((sum, v) => sum + (Number(verticalBudgets[v]) || 0), 0);
  }, [verticalBudgets, selectedVerticals, isMultiBrand, detectedBrands]);

  // Load projections
  const loadProjections = useCallback(async () => {
    if (!selectedVerticals.length || totalBudget <= 0) return;
    setIsLoadingProjection(true);
    try {
      // Combine all zip rows regardless of brand for projections
      const combinedBudgets = isMultiBrand
        ? selectedVerticals.reduce((acc, v) => {
            acc[v] = detectedBrands.reduce((s, b) => s + (Number((verticalBudgets[b] || {})[v]) || 0), 0);
            return acc;
          }, {})
        : verticalBudgets;

      // Applicants who asked for ZIP help still see the budget-based estimate.
      const [estimateRes, demoRes] = await Promise.all([
        fetchProspectEstimate({
          budget: totalBudget,
          verticals: selectedVerticals,
          vertical_budgets: combinedBudgets,
          scenario: 'base',
          zips: zipRows.map((r) => r.zip),
        }),
        zipRows.length
          ? fetchProspectZipDemographics({ zip_rows: zipRows, verticals: selectedVerticals })
          : Promise.resolve(null),
      ]);
      setProjectionSummary(estimateRes?.data?.summary || null);
      setDemographics(demoRes?.data || null);
    } catch {
      toast.error('Failed to load projections. Please try again.');
    } finally {
      setIsLoadingProjection(false);
    }
  }, [zipRows, selectedVerticals, verticalBudgets, totalBudget, isMultiBrand, detectedBrands]);

  const handleNext = () => {
    if (activeStep === 2) {
      // Moving to verticals — pre-select from ZIP data
      initVerticals();
    }
    if (activeStep === 4) {
      // Moving to projections
      setActiveStep(5);
      loadProjections();
      return;
    }
    setActiveStep((p) => p + 1);
  };

  const handleBack = () => setActiveStep((p) => p - 1);

  const handleToggleVertical = (v) => {
    setSelectedVerticals((prev) => prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      let res;
      if (isMultiBrand) {
        const brands = detectedBrands.map((brand) => {
          const brandRows = zipRows.filter((r) => r.brand === brand);
          const bBudgets = verticalBudgets[brand] || {};
          const brandVerticals = selectedVerticals.filter((v) => Number(bBudgets[v]) > 0);
          const brandTotal = brandVerticals.reduce((s, v) => s + (Number(bBudgets[v]) || 0), 0);
          return {
            brand_name: brand,
            verticals: brandVerticals,
            zip_data: brandRows,
            budget: brandTotal,
            vertical_budgets: Object.fromEntries(brandVerticals.map((v) => [v, bBudgets[v]])),
            zip_rows_snapshot: brandRows,
          };
        });
        res = await submitProspect({
          ...contactForm,
          prospect_token: prospectToken,
          zip_help_request: helpRequest,
          brands,
          projection_snapshot: projectionSummary,
          demographics_snapshot: demographics ? { territories: demographics.territories, totals: demographics.totals } : null,
        });
      } else {
        res = await submitProspect({
          ...contactForm,
          prospect_token: prospectToken,
          zip_help_request: helpRequest,
          verticals: selectedVerticals,
          zip_data: zipRows,
          budget: totalBudget,
          vertical_budgets: verticalBudgets,
          projection_snapshot: projectionSummary,
          demographics_snapshot: demographics ? { territories: demographics.territories, totals: demographics.totals } : null,
          zip_rows_snapshot: zipRows,
        });
      }
      if (res?.data?.projections_url) setProjectionsUrl(res.data.projections_url);
      setSubmitted(true);
    } catch (err) {
      const zipError = err?.response?.data?.zip_data;
      toast.error(zipError ? String(zipError) : 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const canProceed = useMemo(() => {
    switch (activeStep) {
      case 0:
        return true;
      case 1:
        return !!(contactForm.company_name && contactForm.contact_name && isValidEmail(contactForm.contact_email));
      case 2:
        // Real ZIPs, or a description of the area for our team to build.
        return zipRows.length > 0 || helpRequest.length >= 10;
      case 3:
        return selectedVerticals.length > 0;
      case 4:
        return totalBudget > 0;
      case 5:
        return !!projectionSummary;
      default:
        return false;
    }
  }, [activeStep, contactForm, zipRows, helpRequest, selectedVerticals, totalBudget, projectionSummary]);

  if (submitted) {
    const leads = projectionSummary?.estimated_leads ? Math.round(projectionSummary.estimated_leads) : null;
    const revenue = projectionSummary?.estimated_revenue ? fmtCurrency(projectionSummary.estimated_revenue) : null;

    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa' }}>
        <Helmet><title>Application Received | {config.SITE.title}</title></Helmet>

        <Box sx={{ bgcolor: '#111827', py: 2, px: 3 }}>
          <Container maxWidth="lg">
            <img src={leadGurusLogo} alt="Lead Gurus" style={{ height: 28 }} />
          </Container>
        </Box>

        <Container maxWidth="sm" sx={{ py: 6 }}>
          <Card sx={{ textAlign: 'center', p: 4 }}>
            <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h4" fontWeight={700} gutterBottom>Application Received</Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Thank you for your interest in the Lead Gurus Exclusive Branded Leads program.
              Our team will review your territory, market fit, and availability. If your application is a fit, we'll be in touch at <strong>{contactForm.contact_email}</strong>.
            </Typography>

            <Divider sx={{ my: 3 }} />

            {/* Summary metrics */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">Company</Typography>
                <Typography fontWeight={600} variant="body2">{contactForm.company_name}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">Verticals</Typography>
                <Typography fontWeight={600} variant="body2">{selectedVerticals.join(', ')}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">ZIP Codes</Typography>
                <Typography fontWeight={600} variant="body2">{zipCount || 'We\'ll build it with you'}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">Monthly Budget</Typography>
                <Typography fontWeight={600} variant="body2">{fmtCurrency(totalBudget)}</Typography>
              </Grid>
            </Grid>

            {(leads || revenue) && (
              <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: '#f5f5f5' }}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>Your Estimated Projections</Typography>
                <Stack direction="row" justifyContent="center" spacing={4}>
                  {leads && (
                    <Box>
                      <Typography variant="h5" fontWeight={700}>{leads}</Typography>
                      <Typography variant="caption" color="text.secondary">Leads/mo</Typography>
                    </Box>
                  )}
                  {revenue && (
                    <Box>
                      <Typography variant="h5" fontWeight={700}>{revenue}</Typography>
                      <Typography variant="caption" color="text.secondary">Est. Revenue/mo</Typography>
                    </Box>
                  )}
                </Stack>
              </Paper>
            )}

            {projectionsUrl && (
              <Button
                variant="contained"
                href={projectionsUrl}
                target="_blank"
                rel="noopener"
                sx={{ mb: 3 }}
              >
                View Full Projections
              </Button>
            )}

            <Divider sx={{ my: 3 }} />

            {/* What happens next */}
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>What Happens Next</Typography>
            <Stack spacing={1.5} sx={{ textAlign: 'left' }}>
              {[
                { num: '1', title: 'Application Review', text: 'Our team evaluates your territory, market overlap, and fit for the program' },
                { num: '2', title: 'Decision & Outreach', text: 'If there\'s availability in your market, we\'ll reach out to schedule a discovery call' },
                { num: '3', title: 'Contract & Onboarding', text: 'Sign your agreement and we handle the rest' },
                { num: '4', title: 'Launch', text: 'Leads start flowing within 7-10 business days of signing' },
              ].map((s) => (
                <Stack key={s.num} direction="row" spacing={1.5} alignItems="flex-start">
                  <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: '#ff680d', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 600, flexShrink: 0, mt: 0.25 }}>
                    {s.num}
                  </Box>
                  <Typography variant="body2"><strong>{s.title}</strong> — {s.text}</Typography>
                </Stack>
              ))}
            </Stack>

            {projectionsUrl && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3 }}>
                Share your projections with your team: <a href={projectionsUrl} target="_blank" rel="noopener" style={{ color: '#ff680d' }}>{projectionsUrl}</a>
              </Typography>
            )}
          </Card>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa' }}>
      <Helmet><title>Get Started | {config.SITE.title}</title></Helmet>

      {/* Header */}
      <Box sx={{ bgcolor: '#111827', py: 2, px: 3 }}>
        <Container maxWidth="lg">
          <img src={leadGurusLogo} alt="Lead Gurus" style={{ height: 28 }} />
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: 4 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>Apply to Join Lead Gurus</Typography>
        <Typography color="text.secondary" sx={{ mb: 4 }}>
          Submit your details to apply for our exclusive branded leads program. We review every application to ensure a great fit for both sides.
        </Typography>

        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {STEPS.map((label) => (
            <Step key={label}><StepLabel>{label}</StepLabel></Step>
          ))}
        </Stepper>

        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ p: 3 }}>
            {activeStep === 0 && <StepIntro />}
            {activeStep === 1 && <StepContact form={contactForm} onChange={setContactForm} />}
            {activeStep === 2 && <ZipIntakeStep value={zipState} onChange={updateZipState} />}
            {activeStep === 3 && (
              <StepVerticals
                available={availableVerticals}
                selected={selectedVerticals}
                onToggle={handleToggleVertical}
                detectedVerticals={detectedVerticals}
              />
            )}
            {activeStep === 4 && (
              <StepBudget
                selectedVerticals={selectedVerticals}
                isMultiBrand={isMultiBrand}
                detectedBrands={detectedBrands}
                verticalBudgets={verticalBudgets}
                setVerticalBudgets={setVerticalBudgets}
                totalBudget={totalBudget}
              />
            )}
            {activeStep === 5 && (
              <StepProjections
                summary={projectionSummary}
                demographics={demographics}
                isLoading={isLoadingProjection}
                onRefresh={loadProjections}
              />
            )}
          </CardContent>
        </Card>

        <Stack direction="row" justifyContent="space-between">
          <Button disabled={activeStep === 0} onClick={handleBack} variant="outlined">Back</Button>
          {activeStep < STEPS.length - 1 ? (
            <Button onClick={handleNext} variant="contained" disabled={!canProceed}>
              {activeStep === 0 ? 'Start Application' : 'Next'}
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={!canProceed || isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={18} /> : <RocketLaunchIcon />}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Application'}
            </Button>
          )}
        </Stack>
      </Container>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Step 0: Intro — Program Overview
// ---------------------------------------------------------------------------
function StepIntro() {
  const highlights = [
    { icon: <VerifiedIcon color="primary" />, title: 'Exclusive Branded Leads', text: 'Every lead is generated under your brand — your name, your offer, your landing page. No shared or resold leads, ever.' },
    { icon: <StarIcon color="primary" />, title: 'Limited Partners Per Market', text: 'We\'re selective about how many partners we take on in each market. Availability depends on the region, vertical, and our existing client base — so not every application is approved. We prioritize companies with the capacity and ambition to scale, not just test the waters.' },
    { icon: <CheckCircleIcon color="primary" />, title: '150+ Clients Across 15+ Home Improvement Verticals', text: 'We manage over $8M/month in ad spend across roofing, windows, bath, solar, HVAC, and more. That scale gives us data and buying power that smaller agencies can\'t match — better CPLs, proven creative, and a self-book system that converts 3-5x higher than standard leads.' },
  ];

  return (
    <Stack spacing={3}>
      <Typography variant="h5" fontWeight={700}>The Lead Gurus Exclusive Branded Leads Program</Typography>
      <Typography color="text.secondary">
        Lead Gurus partners with top home-service companies across the country to deliver high-quality, exclusive leads generated entirely under your brand.
        We don't work with everyone — we review every application to make sure there's a strong fit for both sides before we move forward.
      </Typography>

      <Divider />

      <Stack spacing={3}>
        {highlights.map((h) => (
          <Stack key={h.title} direction="row" spacing={2} alignItems="flex-start">
            <Box sx={{ mt: 0.5 }}>{h.icon}</Box>
            <Box>
              <Typography fontWeight={600}>{h.title}</Typography>
              <Typography variant="body2" color="text.secondary">{h.text}</Typography>
            </Box>
          </Stack>
        ))}
      </Stack>

      <Divider />

      <Paper variant="outlined" sx={{ p: 2.5, bgcolor: '#f8fdf9' }}>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>What we look for in a partner</Typography>
        <Typography variant="body2" color="text.secondary" component="ul" sx={{ pl: 2, mb: 0 }}>
          <li>A minimum monthly ad budget of $15,000 ($10,000+ per vertical)</li>
          <li>A sizable service area — the larger the market, the better our campaigns perform. We generally require a combined market population of at least 1-2 million people</li>
          <li>Service coverage across a defined set of ZIP codes</li>
          <li>A team that can follow up on leads quickly — speed to lead matters</li>
          <li>Willingness to let our proven creative and self-book system work</li>
        </Typography>
      </Paper>

      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
        This application takes about 5 minutes. You'll upload your service area, select your verticals, set your budget, and see estimated lead projections — all before you submit.
      </Typography>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Step 1: Contact Info
// ---------------------------------------------------------------------------
function formatPhone(value) {
  const digits = (value || '').replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function StepContact({ form, onChange }) {
  const update = (field) => (e) => onChange({ ...form, [field]: e.target.value });
  return (
    <Stack spacing={3}>
      <Typography variant="h6">Your Contact Information</Typography>
      <Typography variant="body2" color="text.secondary">
        Tell us about yourself so our team can review your application.
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField label="Company Name" value={form.company_name} onChange={update('company_name')} fullWidth required />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField label="Your Name" value={form.contact_name} onChange={update('contact_name')} fullWidth required />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Email"
            type="email"
            value={form.contact_email}
            onChange={update('contact_email')}
            fullWidth
            required
            error={!!form.contact_email && !isValidEmail(form.contact_email)}
            helperText={form.contact_email && !isValidEmail(form.contact_email) ? 'Enter a valid email address' : ''}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Phone (optional)"
            placeholder="(555) 123-4567"
            value={form.phone}
            onChange={(e) => onChange({ ...form, phone: formatPhone(e.target.value) })}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Company Website"
            placeholder="yourcompany.com"
            value={form.website}
            onChange={update('website')}
            fullWidth
            helperText="Used to pull your logo, brand colors, and privacy policy"
          />
        </Grid>
      </Grid>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Step 2: Verticals (confirm / correct)
// ---------------------------------------------------------------------------
function StepVerticals({ available, selected, onToggle, detectedVerticals }) {
  const unmatched = detectedVerticals.filter(
    (dv) => !available.some((av) => av.toLowerCase() === dv.toLowerCase())
  );

  return (
    <Stack spacing={3}>
      <Typography variant="h6">Confirm Your Verticals</Typography>
      <Typography variant="body2" color="text.secondary">
        We pre-selected verticals based on your ZIP data. Review and adjust as needed.
      </Typography>

      {unmatched.length > 0 && (
        <Alert severity="warning" variant="outlined">
          The following verticals from your ZIP data didn't match our list and were not pre-selected:{' '}
          <strong>{unmatched.join(', ')}</strong>. Please select the correct ones below.
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {available.map((v) => (
          <Chip
            key={v}
            label={v}
            onClick={() => onToggle(v)}
            color={selected.includes(v) ? 'primary' : 'default'}
            variant={selected.includes(v) ? 'filled' : 'outlined'}
            sx={{ fontSize: 14, py: 2, px: 1 }}
          />
        ))}
        {available.length === 0 && <Typography variant="body2" color="text.secondary">Loading verticals...</Typography>}
      </Box>

      {selected.length > 0 && (
        <Alert severity="success" variant="outlined">
          Selected: <strong>{selected.join(', ')}</strong>
        </Alert>
      )}
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Step 3: Budget
// ---------------------------------------------------------------------------
function StepBudget({ selectedVerticals, isMultiBrand, detectedBrands, verticalBudgets, setVerticalBudgets, totalBudget }) {
  const setBrandVerticalBudget = (brand, vertical, value) => {
    setVerticalBudgets((prev) => ({
      ...prev,
      [brand]: { ...(prev[brand] || {}), [vertical]: value },
    }));
  };

  const setSingleVerticalBudget = (vertical, value) => {
    setVerticalBudgets((prev) => ({ ...prev, [vertical]: value }));
  };

  return (
    <Stack spacing={3}>
      <Typography variant="h6">Your Monthly Investment</Typography>
      <Typography variant="body2" color="text.secondary">
        {isMultiBrand
          ? 'As part of your application to the Lead Gurus Exclusive Branded Leads program, indicate the monthly budget you\'re prepared to invest per vertical for each brand. This helps our team evaluate fit and availability in your target markets.'
          : 'As part of your application to the Lead Gurus Exclusive Branded Leads program, indicate the monthly budget you\'re prepared to invest per vertical. This helps our team evaluate fit and availability in your target markets.'}
      </Typography>

      <Alert severity="info" variant="outlined">
        Our minimum total investment is <strong>$15,000/month</strong> with a minimum of <strong>$10,000/month per vertical</strong>.
        Spots in each market are limited — we cap the number of partners per vertical and region to protect lead quality and exclusivity.
      </Alert>

      {isMultiBrand ? (
        detectedBrands.map((brand) => {
          const bBudgets = verticalBudgets[brand] || {};
          const brandTotal = selectedVerticals.reduce((s, v) => s + (Number(bBudgets[v]) || 0), 0);
          return (
            <Paper key={brand} variant="outlined" sx={{ p: 2 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography fontWeight={600}>{brand}</Typography>
                <Typography variant="body2" color="text.secondary">{fmtCurrency(brandTotal)}/mo</Typography>
              </Stack>
              <Grid container spacing={2}>
                {selectedVerticals.map((v) => (
                  <Grid item xs={12} sm={6} key={v}>
                    <TextField
                      label={v}
                      type="number"
                      value={bBudgets[v] || ''}
                      onChange={(e) => setBrandVerticalBudget(brand, v, e.target.value)}
                      fullWidth
                      size="small"
                      InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                    />
                  </Grid>
                ))}
              </Grid>
            </Paper>
          );
        })
      ) : (
        <Grid container spacing={2}>
          {selectedVerticals.map((v) => (
            <Grid item xs={12} sm={6} key={v}>
              <TextField
                label={v}
                type="number"
                value={verticalBudgets[v] || ''}
                onChange={(e) => setSingleVerticalBudget(v, e.target.value)}
                fullWidth
                size="small"
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              />
            </Grid>
          ))}
        </Grid>
      )}

      <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f5f5f5', textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">Total Monthly Investment</Typography>
        <Typography variant="h5" fontWeight={700}>{fmtCurrency(totalBudget)}</Typography>
      </Paper>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Step 4: Projections
// ---------------------------------------------------------------------------
function StepProjections({ summary, demographics, isLoading, onRefresh }) {
  if (isLoading) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <CircularProgress size={48} sx={{ mb: 2 }} />
        <Typography color="text.secondary">Calculating your projections...</Typography>
      </Box>
    );
  }

  if (!summary) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <Typography color="text.secondary">No projection data available.</Typography>
        <Button onClick={onRefresh} sx={{ mt: 2 }}>Retry</Button>
      </Box>
    );
  }

  const features = demographics?.features || [];
  const territories = demographics?.territories || [];
  const totals = demographics?.totals || {};
  const a = summary.assumptions || {};
  const leads = Math.round(summary.estimated_leads || 0);
  const setAppts = summary.verticals?.reduce((s, v) => s + (v.set_appointments || 0), 0);
  const issues = summary.verticals?.reduce((s, v) => s + (v.issues || 0), 0);
  const demos = summary.verticals?.reduce((s, v) => s + (v.demos || 0), 0);
  const netSold = summary.verticals?.reduce((s, v) => s + (v.net_sold || 0), 0);
  const comPct = summary.estimated_revenue ? ((summary.budget / summary.estimated_revenue) * 100).toFixed(1) + '%' : '—';

  const blocks = [
    { label: 'Total Budget', value: fmtCurrency(summary.budget), sub: summary.budget ? `~${fmtCurrency(summary.budget / 30)}/day` : null },
    { label: 'Leads & CPL', value: `${fmt(leads)} / ${summary.assumed_cpl ? fmtCurrency(summary.assumed_cpl) : '—'}`, sub: 'Est. leads / Blended CPL' },
    { label: 'Set Appointments', value: fmtDec(setAppts), sub: a.set_rate ? `Set rate ${fmtPct(a.set_rate)}` : null },
    { label: 'Issues', value: fmtDec(issues), sub: a.issue_rate ? `Issue rate ${fmtPct(a.issue_rate)}` : null },
    { label: 'Demos', value: fmtDec(demos), sub: a.demo_rate ? `Demo rate ${fmtPct(a.demo_rate)}` : null },
    { label: 'Net Sold', value: fmtDec(netSold), sub: a.net_close_rate ? `Close rate ${fmtPct(a.net_close_rate)}` : null },
    { label: 'Avg Job Size', value: a.avg_job_size ? fmtCurrency(a.avg_job_size) : '—' },
    { label: 'Net Revenue', value: fmtCurrency(summary.estimated_revenue) },
    { label: 'Cost of Marketing', value: comPct },
  ];

  return (
    <Stack spacing={3}>
      <Typography variant="h6">Your Projections</Typography>

      {totals.population != null && totals.population < 1000000 && (
        <Alert severity="warning" variant="outlined">
          Your total serviceable population is <strong>{fmt(totals.population)}</strong>, which is under 1 million.
          Adding more ZIP codes will improve campaign performance.
        </Alert>
      )}

      <Alert severity="info" variant="outlined">
        These projections are estimates based on historical data and industry benchmarks. Actual results may vary.
      </Alert>

      <Grid container spacing={1.5}>
        {blocks.map((b) => (
          <Grid item xs={6} sm={4} key={b.label}>
            <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, bgcolor: 'grey.50' }}>
              <Typography color="text.secondary" variant="caption">{b.label}</Typography>
              <Typography variant="h6" fontWeight={700}>{b.value ?? '—'}</Typography>
              {b.sub && <Typography color="text.secondary" variant="caption">{b.sub}</Typography>}
            </Paper>
          </Grid>
        ))}
      </Grid>

      {summary.verticals?.length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Vertical</TableCell>
              <TableCell align="right">Budget</TableCell>
              <TableCell align="right">CPL</TableCell>
              <TableCell align="right">Leads</TableCell>
              <TableCell align="right">Set Appts</TableCell>
              <TableCell align="right">Net Sold</TableCell>
              <TableCell align="right">Net Revenue</TableCell>
              <TableCell align="right">CoM%</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {summary.verticals.map((v) => {
              const com = v.net_revenue ? ((v.budget / v.net_revenue) * 100).toFixed(1) + '%' : '—';
              return (
                <TableRow key={v.name}>
                  <TableCell>{v.name}</TableCell>
                  <TableCell align="right">{fmtCurrency(v.budget)}</TableCell>
                  <TableCell align="right">{fmtCurrency(v.scenario_cpl)}</TableCell>
                  <TableCell align="right">{fmtDec(v.leads)}</TableCell>
                  <TableCell align="right">{fmtDec(v.set_appointments)}</TableCell>
                  <TableCell align="right">{fmtDec(v.net_sold)}</TableCell>
                  <TableCell align="right">{fmtCurrency(v.net_revenue)}</TableCell>
                  <TableCell align="right">{com}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      {features.length > 0 && (
        <Box sx={{ height: 400, borderRadius: 2, overflow: 'hidden', border: '1px solid #e0e0e0' }}>
          <ProjectionZipMap features={features} territories={territories} />
        </Box>
      )}

      {territories.length > 0 && (
        <>
          <Typography variant="subtitle2" sx={{ mt: 2 }}>Territory Summary</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Territory</TableCell>
                <TableCell>Vertical</TableCell>
                <TableCell align="right">ZIPs</TableCell>
                <TableCell align="right">Population</TableCell>
                <TableCell align="right">Med. Income</TableCell>
                <TableCell align="right">Med. Home Value</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {territories.map((t, i) => (
                <TableRow key={i}>
                  <TableCell>{t.territory}</TableCell>
                  <TableCell>{t.vertical}</TableCell>
                  <TableCell align="right">{fmt(t.zip_count)}</TableCell>
                  <TableCell align="right">{fmt(t.population)}</TableCell>
                  <TableCell align="right">{fmtCurrency(t.median_income)}</TableCell>
                  <TableCell align="right">{fmtCurrency(t.median_home_value)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}
    </Stack>
  );
}
